/**
 * SIGNAGE BAR — the black status line (cockpit bundle §5bis). Stays BLACK in
 * both themes, so its accents are fixed --bar-* constants, never theme vars.
 * Carries: brand plate, today's focus line (desktop), the nav
 * [g]igs [f]ichiers [r]olodex [réglages] [aide], the honest save state
 * (click = force flush, real), and the mono clock.
 */
import { t, type Locale } from "../i18n.ts";
import type { CockpitView } from "./Console.tsx";

export interface SignageBarProps {
  locale: Locale;
  isDesktop: boolean;
  focusLine: string;
  view: CockpitView;
  canWrite: boolean;
  onView(view: CockpitView): void;
  onStartTutorial(): void;
  /** Honest save state text + tone (derived from pending/flushing/attention). */
  saveText: string;
  saveTone: "ok" | "busy" | "warn";
  onSaveNow(): void;
  nowMs: number;
}

export function SignageBar(p: SignageBarProps) {
  const { locale } = p;
  const clock = `${new Intl.DateTimeFormat(locale === "fr" ? "fr-FR" : "en-GB", {
    weekday: "short",
    day: "2-digit",
    month: "short",
  }).format(new Date(p.nowMs))} · ${new Date(p.nowMs).toLocaleTimeString(locale === "fr" ? "fr-FR" : "en-GB", { hour: "2-digit", minute: "2-digit" })}`;

  // Mobile: single-letter nav — nothing is ever chopped mid-word (Eduardo
  // 2026-07-03: half-visible items read as glitches).
  const nav = (
    view: CockpitView,
    key: "nav.gigs" | "nav.files" | "nav.rolodex" | "nav.settings",
    shortKey: "nav.gigs_s" | "nav.files_s" | "nav.rolodex_s" | "nav.settings_s",
  ) => (
    <button type="button" className={`bar-nav${p.view === view ? " active" : ""}`} onClick={() => p.onView(view)}>
      {t(locale, p.isDesktop ? key : shortKey)}
    </button>
  );

  return (
    <header className="signage-bar">
      <span className="bar-brand">{t(locale, p.isDesktop ? "app.title" : "app.title_short")}</span>
      {p.isDesktop && <span className="bar-focus">{p.focusLine}</span>}
      <span className="bar-spacer" />
      {nav("gig", "nav.gigs", "nav.gigs_s")}
      {nav("vault", "nav.files", "nav.files_s")}
      {p.canWrite && nav("rolo", "nav.rolodex", "nav.rolodex_s")}
      {p.canWrite && nav("settings", "nav.settings", "nav.settings_s")}
      {p.isDesktop && (
        <button type="button" className="bar-nav" onClick={p.onStartTutorial}>
          {t(locale, "nav.help")}
        </button>
      )}
      {p.canWrite && (
        <button
          type="button"
          className={`bar-save ${p.saveTone}`}
          title={t(locale, "save.tooltip")}
          onClick={p.onSaveNow}
        >
          {p.saveText}
        </button>
      )}
      <span className="bar-clock num">{clock}</span>
    </header>
  );
}
